// src/components/UnsplashImage.tsx
import React, { useEffect, useState } from 'react';
import { fetchUniqueUnsplashImage } from '../utils/unsplash';
import { Skeleton } from './Skeleton';

interface UnsplashImageProps {
  query: string;
  alt: string;
  className?: string;
  height?: string;
}

const UnsplashImage: React.FC<UnsplashImageProps> = ({ 
  query, 
  alt, 
  className = "w-full h-64 object-cover rounded-lg", 
  height = "h-64"
}) => {
  const [imageData, setImageData] = useState<{
    url: string;
    photographer: string;
    profile: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchImage = async () => {
      try {
        console.log(`UnsplashImage component fetching image for query: "${query}"`);
        const result = await fetchUniqueUnsplashImage(query);
        
        if (!isMounted) return;
        
        if (result) {
          console.log(`UnsplashImage component received image data for query: "${query}"`);
          setImageData({
            url: result.urls.regular,
            photographer: result.user.name,
            profile: result.user.links.html
          });
          setLoading(false);
        } else {
          console.error(`No images found for query: "${query}"`);
          setError('No images found for this query');
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error(`Error fetching Unsplash image for query: "${query}"`, err);
        setError(err instanceof Error ? err.message : 'Failed to load image');
        setLoading(false);
      }
    };

    if (retryCount < 3) {
      fetchImage();
    } else {
      setLoading(false);
      setError('Maximum retry attempts reached');
    }

    return () => {
      isMounted = false;
    };
  }, [query, retryCount]);

  if (loading) {
    return <Skeleton className={`${className} ${height} bg-gray-200`} />;
  }

  if (error) {
    return (
      <div className={`unsplash-error ${className} ${height} bg-gray-100 flex flex-col items-center justify-center rounded-lg p-4`}>
        <p className="text-gray-500 mb-2">Image unavailable</p>
        <p className="text-xs text-gray-400 mb-3">{error}</p>
        {retryCount < 3 && (
          <button 
            onClick={() => setRetryCount(c => c + 1)}
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
          >
            Retry ({retryCount + 1}/3)
          </button>
        )}
      </div>
    );
  }

  return imageData ? (
    <div className="unsplash-image relative">
      <img
        src={imageData.url}
        alt={alt}
        className={className}
        loading="lazy"
      />
      <div className="attribution absolute bottom-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-tl">
        Photo by{' '}
        <a 
          href={`${imageData.profile}?utm_source=david_sheeshka_website&utm_medium=referral`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {imageData.photographer}
        </a>{' '}
        on{' '}
        <a 
          href="https://unsplash.com/?utm_source=david_sheeshka_website&utm_medium=referral"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Unsplash
        </a>
      </div>
    </div>
  ) : null;
};

export default UnsplashImage;
